import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';

// Pages under the Services menu item, which stays highlighted on any of them.
const SERVICE_PATHS = ['/services', '/gi-pathology', '/histology-training'];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef(null);
  const navRef = useRef(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 16);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(y / max, 1) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      wasOpen.current = true;
      const first = navRef.current?.querySelector('a, input, button');
      first?.focus();
    } else if (wasOpen.current) {
      toggleRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onTab = (event) => {
      if (event.key !== 'Tab') return;
      const panel = navRef.current;
      const toggle = toggleRef.current;
      if (!panel) return;
      const items = [toggle, ...panel.querySelectorAll('a[href], button:not([disabled]), input')].filter(Boolean);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className={`site-header${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-menu-open' : ''}`}>
      <div className="scroll-progress" style={{ transform: `scaleX(${progress})` }} />
      <div className="header-inner">
        <NavLink to="/" end className="logo" onClick={closeMenu}>
          <span className="logo-mark" aria-hidden="true">BD</span>
          <span className="logo-text">
            <span className="logo-text-primary">Boca-Delray</span>
            <span className="logo-text-secondary">Pathology Laboratories</span>
          </span>
        </NavLink>
        <button
          type="button"
          className="menu-toggle"
          ref={toggleRef}
          aria-expanded={menuOpen}
          aria-controls="site-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="menu-toggle-bars" aria-hidden="true" />
          <span className="visually-hidden">{menuOpen ? 'Close menu' : 'Open menu'}</span>
        </button>
        {menuOpen ? <div className="nav-backdrop" onClick={closeMenu} /> : null}
        <div className={`nav-container${menuOpen ? ' is-open' : ''}`} id="site-nav" ref={navRef}>
          <nav aria-label="Primary">
            <ul>
              <li><NavLink to="/" end onClick={closeMenu}>Home</NavLink></li>
              <li className="has-submenu">
                <NavLink
                  to="/services"
                  onClick={closeMenu}
                  className={() => (SERVICE_PATHS.includes(location.pathname) ? 'active' : undefined)}
                >
                  Services
                </NavLink>
                <ul className="submenu">
                  <li><NavLink to="/gi-pathology" onClick={closeMenu}>GI Pathology</NavLink></li>
                  <li><NavLink to="/histology-training" onClick={closeMenu}>Histology Training</NavLink></li>
                </ul>
              </li>
              <li><NavLink to="/referring-practices" onClick={closeMenu}>For Practices</NavLink></li>
              <li><NavLink to="/team" onClick={closeMenu}>Our Team</NavLink></li>
              <li><NavLink to="/contact" onClick={closeMenu}>Contact</NavLink></li>
            </ul>
          </nav>
          <SearchBar onNavigate={closeMenu} />
          <div className="header-actions">
            <a className="call-btn" href="tel:+15616387577">Call (561) 638-7577</a>
            <a
              href="https://bdp.labdynamo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="portal-btn"
            >
              Provider Portal
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
